const post = {
  slug: 'youtube-channel-not-growing',
  cluster: 'strategy',
  eyebrow: 'Strategy',
  title: 'Is your channel failing, or just early? How to tell',
  description:
    'A diagnostic. Which number to look at first, what each failure mode actually looks like, and the one case where the honest answer is that nothing is wrong yet.',
  keywords: [
    'youtube channel not growing', 'why is my channel not growing', 'low youtube views',
    'youtube retention', 'click through rate', 'youtube analytics',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/youtube-channel-not-growing.webp',
    alt: 'Two retention curves side by side: one dropping off a cliff in the first seconds, one bleeding slowly through the middle.',
  },
  lede:
    '"My channel is not growing" is four different problems wearing the same clothes. Each has a different fix, and you can tell them apart with two numbers.',
  sections: [
    {
      heading: 'Start with the funnel, not the view count',
      body: [
        {
          type: 'para',
          text: 'Views are the output of a chain: the video gets shown, someone clicks, they stay, the system shows it to more people. A low view count tells you the chain broke without telling you where.',
        },
        {
          type: 'para',
          text: 'Two numbers locate the break.',
        },
        {
          type: 'list',
          items: [
            '**Click-through rate** — of the people shown the thumbnail, how many clicked.',
            '**Retention shape** — of the people who clicked, where they left. The shape, not the average.',
          ],
        },
        {
          type: 'para',
          text: 'Every diagnosis below is a combination of those two.',
        },
      ],
    },
    {
      heading: 'Low impressions, whatever the CTR',
      body: [
        {
          type: 'para',
          text: 'If the video is barely being *shown*, the problem is upstream of the video. Two usual causes.',
        },
        {
          type: 'para',
          text: 'The first is that nobody is searching for this. A well-made video on a topic with no demand behind it has no audience to be shown to. Check whether the topic is one people look for — [evaluating a niche](/blogs/how-to-evaluate-a-youtube-niche) covers how.',
        },
        {
          type: 'para',
          text: 'The second is that the channel is new and has no history for the system to reason from. This is the case where the honest answer is that nothing is wrong. It resolves with more videos, not with fixes.',
        },
      ],
    },
    {
      heading: 'Decent impressions, low CTR',
      body: [
        {
          type: 'para',
          text: 'You are being shown and not clicked. This is a **packaging** problem, and it is the most fixable thing on this page — the video is untouched, only the title and thumbnail change.',
        },
        {
          type: 'para',
          text: 'Usual causes: the thumbnail does not read at phone size, the title describes the subject rather than the question, or title and thumbnail are saying the same thing twice instead of working as a pair. [Title and thumbnail as one unit](/blogs/youtube-title-and-thumbnail) goes through it.',
        },
        {
          type: 'callout',
          title: 'Do not read CTR in isolation',
          text: 'A high CTR on a video with terrible retention usually means the packaging overpromised. The two numbers only make sense together.',
        },
      ],
    },
    {
      heading: 'Good CTR, cliff in the first thirty seconds',
      body: [
        {
          type: 'para',
          text: 'People clicked and left almost immediately. Something in the opening broke the promise the thumbnail made.',
        },
        {
          type: 'para',
          text: 'Most often the video takes too long to arrive. A channel introduction, a restatement of the title, a slow scene-set — all of it is asking a stranger to wait, and they will not. The other common version is that the video simply is not what the thumbnail implied.',
        },
        {
          type: 'para',
          text: 'The fix is in the first two sentences: ask the question the thumbnail raised, and do not answer it yet. [Hooks](/blogs/youtube-hook-writing) covers the specifics.',
        },
      ],
    },
    {
      heading: 'Good CTR, slow bleed through the middle',
      body: [
        {
          type: 'para',
          text: 'No cliff, just a steady decline from around the two-minute mark. This is the hardest one to see and the most common on well-made faceless videos.',
        },
        {
          type: 'para',
          text: 'It means nothing was pending. The video was informative and correct, each point followed the last, and at no moment did the viewer have a reason to still be there. People do not leave because it got worse; they leave because nothing was unresolved.',
        },
        {
          type: 'para',
          text: 'This is a scripting problem, not an editing one — speeding up the cuts does not fix it. See [open loops](/blogs/youtube-retention-open-loops).',
        },
      ],
    },
    {
      heading: 'Everything looks fine and views are still low',
      body: [
        {
          type: 'para',
          text: 'Reasonable CTR, reasonable retention, few views. Usually this means the videos are competent but interchangeable — nothing about them says which channel they came from or why this treatment of the topic rather than the other forty.',
        },
        {
          type: 'para',
          text: 'The test is the one at the end of [script writing](/blogs/youtube-script-writing): could this video have been about a slightly different topic with a few words swapped? If yes, the fix is specificity, not production.',
        },
      ],
    },
    {
      heading: 'When it is genuinely just early',
      body: [
        {
          type: 'para',
          text: 'Under roughly twenty videos, with no clear pattern in either direction, the honest reading is that you do not have enough signal yet. Variance at low volume is enormous — one video doing five times another is normal noise, not a finding.',
        },
        {
          type: 'para',
          text: 'The useful thing to do in that window is not to optimise. It is to publish more, hold the format steady so the results stay comparable, and revisit at twenty. See [what your first twenty videos are for](/blogs/first-20-youtube-videos).',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'What is a good click-through rate?',
      a: 'It varies enormously by how the video is being surfaced — search impressions and suggested impressions behave differently, and a channel\'s own subscribers click at a different rate to strangers. Compare a video against your own other videos rather than against a number from an article.',
    },
    {
      q: 'Should I delete videos that did badly?',
      a: 'Generally no. They cost nothing to leave up, they sometimes find an audience much later, and they are the comparison set you need to tell a pattern from noise.',
    },
    {
      q: 'Does changing the thumbnail on an old video help?',
      a: 'It can, and it is cheap to try. A video with good retention and poor CTR is the best candidate: you already know people who watch it stay, so the only broken link is the click.',
    },
  ],
  related: ['first-20-youtube-videos', 'youtube-title-and-thumbnail', 'youtube-retention-open-loops'],
};

export default post;
